
import { defineComponent } from 'vue';
import { getStatement, getStatements } from '@/api/statements';
import { Statement, StatementsResponse } from '@/interfaces/statement';
import PaginationControls from '@/components/common/PaginationControls.vue';
import PageLoader from '@/components/common/PageLoader.vue';
import { formatDateReadable } from '@/utils/dates';
import { GlobalAlertTypes } from '~/plugins/alerts/types';

type GroupedStatement = {
  year: number;
  statements: Statement[];
};

export default defineComponent({
  name: 'ConsumerStatements',

  components: { PageLoader, PaginationControls },

  data: () => ({
    currentPageNumber: 1,
    statements: {
      count: 0,
      next: null,
      previous: null,
      results: [],
    } as StatementsResponse,
    loading: true,
  }),

  async mounted() {
    await this.loadStatements();
  },

  methods: {
    async loadStatements() {
      this.loading = true;
      try {
        const { data } = await getStatements(this.loanNumber, this.currentPageNumber);
        this.statements = data;
      } catch {
        this.$alerts.add({
          type: GlobalAlertTypes.ERROR,
          text: 'Failed to fetch statements',
          timed: true,
        });
      } finally {
        this.loading = false;
      }
    },

    async openStatement(statementId: number) {
      try {
        const { data } = await getStatement(this.loanNumber, statementId);
        if (data.consumer_statement_link === null) throw Error('Invalid link');
        window.open(data.consumer_statement_link, '_blank');
      } catch {
        this.$alerts.add({
          type: GlobalAlertTypes.ERROR,
          text: 'Failed to fetch document',
          timed: true,
        });
      }
    },

    formatDateReadable(date: string): string {
      return formatDateReadable(date);
    }
  },

  computed: {
    loanNumber(): string {
      return this.$store.getters['User/activeLoanNumber'];
    },

    statementsGroupedByYear(): GroupedStatement[] {
      return this.statements.results.reduce((accumulator: GroupedStatement[], item: Statement) => {
        const existingGroupIndex = accumulator.findIndex(g => {
          return g.year === new Date(item.statement_date).getFullYear();
        });

        if (existingGroupIndex !== -1) {
          accumulator[existingGroupIndex].statements.push(item);
        } else {
          accumulator.push({
            year: new Date(item.statement_date).getFullYear(),
            statements: [item],
          });
        }

        return accumulator;
      }, [])
        .sort((a: GroupedStatement, b: GroupedStatement) => {
          return b.year - a.year;
        });
    }
  },

  watch: {
    currentPageNumber() {
      this.loadStatements();
    }
  }
});
